import React, { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import LinkOff from "@mui/icons-material/LinkOff";
import { SmartTextField } from "../../core/SmartTextField";
import { CircularProgressSize } from "../../theme";
import { useMergedPublishers } from "../../publishers/use-merged-publishers";

/**
 * Trims the name and returns undefined for empty/all-whitespace input so
 * SmartTextField can show an error state before the user submits.
 *
 * @param {string} name
 * @returns {string|undefined}
 */
export const sanitisePublisherName = (name) => {
  if (!name) return undefined;
  const trimmed = name.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};

/**
 * @param {object} props
 * @param {(name: string) => Promise<void>} props.onCreate - Called with the entered name when the user clicks Add.
 * @param {boolean} props.isPending - Whether the create mutation is in-flight.
 * @param {(name: string) => void} props.onNameChange - callback so the parent can sync the publisher list filter.
 */
export const CustomPublisherCard = ({ onCreate, isPending, onNameChange }) => {
  const [publisherName, setPublisherName] = useState("");
  const [inputKey, setInputKey] = useState(0);
  const { data: publishers = [] } = useMergedPublishers();

  // Intentionally returns 0 for empty input — every string contains "" so we
  // must guard here to avoid matching all publishers when nothing is typed.
  const displayCount = useMemo(() => {
    if (!publisherName) {
      return 0;
    }

    const toMatch = publisherName.toLowerCase();
    return publishers.filter(([publisher]) => publisher.name.toLowerCase().includes(toMatch)).length;
  }, [publisherName, publishers]);

  const infoOverWarning = displayCount === 0 || publisherName.length === 0;

  const handleNameChange = (name) => {
    setPublisherName(name);
    onNameChange?.(name);
  };

  const handleAdd = async () => {
    if (!publisherName) return;
    await onCreate(publisherName);
    setPublisherName("");
    setInputKey((k) => k + 1);
    onNameChange?.("");
  };

  return (
    <Card elevation={2}>
      <CardHeader avatar={<LinkOff />} title="Custom Publishers" />
      <CardContent>
        <Typography>
          Can&apos;t find a publisher? Add them below. Any publisher you add will be visible only to you and your
          co-designers. Once you have added a publisher once you can use them again for other games.
        </Typography>
        <Typography paddingTop={1}>
          We will check manually added publishers and if we can find enough information about them we will convert them
          into public records everyone can use.
        </Typography>
        <Box p={1} />
        <SmartTextField
          key={inputKey}
          required={false}
          initialValue=""
          label="Publisher Name"
          helperText=""
          autoComplete="off"
          autoFocus={false}
          sanitize={sanitisePublisherName}
          onChange={handleNameChange}
          onEnter={handleNameChange}
        />
        <Alert severity={infoOverWarning ? "info" : "warning"}>
          {infoOverWarning
            ? "As you type, we will search for publishers with similar names."
            : `We found ${displayCount} publishers with similar names. Please confirm the publisher you are looking for is not in the list. You may need to change your filters to find the matching publishers.`}
        </Alert>
      </CardContent>
      <CardActions>
        <Button disabled={publisherName.length === 0 || isPending} onClick={handleAdd}>
          {isPending ? <CircularProgress size={CircularProgressSize} /> : "Add Publisher"}
        </Button>
      </CardActions>
    </Card>
  );
};
